/** Lead display helpers: stage labels/tones, contact name, and the studio's
 *  "days to X" due convention (negative = overdue, positive = upcoming). */

import type { Lead, LeadStage } from "@/types";
import type { StatusTone } from "./status";

/** Stages with a face of their own — everything except legacy `consulted`,
 *  which effectiveStage folds into `qualified` before any lookup. */
type DisplayStage = Exclude<LeadStage, "consulted">;

/** The Xano consultation endpoint (held) still writes the legacy `consulted`
 *  stage; its board column duplicated Qualified and was dropped, so consulted
 *  leads bucket into and read as Qualified everywhere. */
export function effectiveStage(stage: LeadStage): DisplayStage {
  return stage === "consulted" ? "qualified" : stage;
}

export const STAGE_LABEL: Record<DisplayStage, string> = {
  new: "New",
  to_schedule: "To schedule",
  scheduled: "Scheduled",
  qualified: "Qualified",
  quoted: "Quoted",
  disqualified: "Disqualified",
  lost: "Lost",
};

/** Column sub-heading framing (board worklist language). */
export const STAGE_HINT: Partial<Record<LeadStage, string>> = {
  to_schedule: "consultations to arrange",
  scheduled: "consultations to have",
};

const STAGE_TONE: Record<DisplayStage, StatusTone> = {
  new: "info-soft",
  to_schedule: "info",
  scheduled: "info",
  qualified: "success",
  quoted: "success",
  disqualified: "neutral",
  lost: "danger",
};

export function stageLabel(stage: LeadStage): string {
  return STAGE_LABEL[effectiveStage(stage)] ?? stage;
}

export function stageTone(stage: LeadStage): StatusTone {
  return STAGE_TONE[effectiveStage(stage)] ?? "neutral";
}

/** Search over the fields staff actually type into the board/list search box:
 *  name, company, email, postcode (case-insensitive substring). */
export function matchesQuery(lead: Lead, q: string): boolean {
  if (!q) return true;
  const hay = [
    lead.contact_first_name,
    lead.contact_last_name,
    lead.company_name,
    lead.email,
    lead.site_postcode,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(q.toLowerCase());
}

/** Display name: company when is_company, else person; falls back to email. */
export function leadName(lead: Pick<Lead, "contact_first_name" | "contact_last_name" | "company_name" | "is_company" | "email">): string {
  const person = [lead.contact_first_name, lead.contact_last_name].filter(Boolean).join(" ").trim();
  if (lead.is_company && lead.company_name) return lead.company_name;
  return person || lead.company_name || lead.email || "Unnamed lead";
}

/** epoch ms with 0/unset → has a value? */
export function isSet(ts: number | undefined | null): boolean {
  return typeof ts === "number" && ts > 0;
}

/** Open = still being worked: on the pipeline (no off-ramp) and not won
 *  (archived by quote provisioning). */
export function isOpenLead(lead: Lead): boolean {
  return !isSet(lead.archived_at) && lead.stage !== "disqualified" && lead.stage !== "lost";
}

export interface DueInfo {
  days: number; // calendar days from today: negative past, 0 today, positive future
  label: string;
  tone: StatusTone;
}

/** "days to X" for next_action_at; null when unset. */
export function dueInfo(ts: number | undefined | null): DueInfo | null {
  if (!isSet(ts)) return null;
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(new Date(ts as number)) - startOfDay(new Date())) / 86_400_000);
  let label: string;
  if (days < 0) label = `${Math.abs(days)}d overdue`;
  else if (days === 0) label = "Due today";
  else label = `Due in ${days}d`;
  const tone: StatusTone = days < 0 ? "danger" : days <= 2 ? "info" : "neutral";
  return { days, label, tone };
}
